import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Droplets, Clock, Loader2, CheckCircle2, Car, DollarSign, Receipt, TrendingUp, Calendar } from 'lucide-react';
import ComparativaMensual from '@/componentes/ComparativaMensual';

export default function CarwashDashboard() {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/carwash/dashboard');
      setData(res.data);
    } catch { /* silent */ }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); const i = setInterval(cargar, 30000); return () => clearInterval(i); }, [cargar]);

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 bg-slate-800" />)}</div></div>;

  if (!data) return null;

  const metricas = [
    { titulo: 'Total Hoy', valor: data.total_turnos, icono: Car, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    { titulo: 'En Espera', valor: data.en_espera, icono: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { titulo: 'Lavando', valor: data.lavando, icono: Loader2, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { titulo: 'Listos', valor: data.listos, icono: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10' },
    { titulo: 'Entregados', valor: data.entregados, icono: Droplets, color: 'text-violet-400', bg: 'bg-violet-500/10' },
    { titulo: 'Ingresos Hoy', valor: formatoMXN(data.ingresos_hoy), icono: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { titulo: 'Ingresos del Mes', valor: formatoMXN(data.ingresos_mes || 0), icono: Calendar, color: 'text-emerald-400', bg: 'bg-emerald-500/10', variacion: data.variacion_pct },
    { titulo: 'Cobrados', valor: data.total_cobrado, icono: Receipt, color: 'text-teal-400', bg: 'bg-teal-500/10' },
    { titulo: 'Ticket Prom.', valor: formatoMXN(data.ticket_promedio), icono: TrendingUp, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-dashboard-page">
      <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
        <Droplets className="w-8 h-8 text-cyan-400" /> Dashboard Car Wash
      </h1>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {metricas.map((m, i) => {
          const Icon = m.icono;
          return (
            <Card key={i} className="bg-slate-900/40 border-slate-800" data-testid={`cw-metric-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1.5">{m.titulo}</p>
                    <p className="text-2xl font-bold text-slate-50 font-barlow">{m.valor}</p>
                    {m.variacion !== undefined && m.variacion !== null && (
                      <p className={`text-[10px] mt-1 font-medium ${
                        m.variacion > 0 ? 'text-emerald-400' : m.variacion < 0 ? 'text-red-400' : 'text-slate-500'
                      }`} data-testid="cw-variacion-mes-badge">
                        {m.variacion > 0 ? '▲' : m.variacion < 0 ? '▼' : '='} {Math.abs(m.variacion)}% vs mes ant.
                      </p>
                    )}
                  </div>
                  <div className={`w-9 h-9 rounded-sm ${m.bg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${m.color}`} strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {data.pendientes_cobro > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-amber-400" />
            <p className="text-sm text-amber-300"><span className="font-bold">{data.pendientes_cobro}</span> turno(s) pendientes de cobro</p>
          </CardContent>
        </Card>
      )}

      {Object.keys(data.ingresos_servicio).length > 0 && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Ingresos por Servicio</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.ingresos_servicio).map(([nombre, info]) => (
                <div key={nombre} className="flex items-center justify-between p-3 bg-slate-800/40 rounded-sm">
                  <div>
                    <p className="text-sm text-slate-200 font-medium">{nombre}</p>
                    <p className="text-xs text-slate-500">{info.count} lavado(s)</p>
                  </div>
                  <span className="text-sm text-cyan-400 font-mono font-bold">{formatoMXN(info.total)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparativo mensual */}
      <ComparativaMensual etiquetaOperaciones="Turnos" />
    </div>
  );
}
